import { useState, useEffect, useRef } from 'react';
import { LIMITES } from '../../../utils/constantes';

// Função helper para calcular diferença em meses
const calcularDiferencaMeses = (mes1, ano1, mes2, ano2) => {
  return (ano2 - ano1) * 12 + (mes2 - mes1);
};

export const useIntermediateTableLogic = (onIntermediatesChange, maxLimit, initialIntermediates = [], replicateValue = true, initialMonthOffset = 3, isFlex = false) => {
    const [intermediates, setIntermediates] = useState([]);
    const prevInitialRef = useRef([]);
    const lastEditTimeRef = useRef(0);
    const EDIT_BLOCK_DURATION = 1500;
    
    // Use o limite fornecido ou o padrão da carteira
    const limit = maxLimit === undefined ? LIMITES.INTERMEDIARIAS : maxLimit;

    // Verificar se estamos no período de bloqueio de atualizações externas
    const isInEditBlockPeriod = () => {
        return Date.now() - lastEditTimeRef.current < EDIT_BLOCK_DURATION;
    };

    // Marcar que uma edição aconteceu
    const markEditTime = () => {
        lastEditTimeRef.current = Date.now();
    };

    // Inicialização e processamento de dados externos
    useEffect(() => {
        // Não processar atualizações externas durante o período de edição
        if (isInEditBlockPeriod()) return;

        // Verificar se initialIntermediates mudou realmente
        if (
            initialIntermediates?.length > 0 &&
            JSON.stringify(prevInitialRef.current) !== JSON.stringify(initialIntermediates)
        ) {
            prevInitialRef.current = structuredClone(initialIntermediates);

            // Reaproveita o id por posição quando o pai não envia, evitando remount do <select> via key
            setIntermediates(prev => initialIntermediates.map((item, index) => ({
                id: item.id || prev[index]?.id || Date.now() + index,
                description: `Parcela Intermediária ${index + 1}`,
                value: item.Valor === undefined ? 0 : item.Valor,
                mesIntermediaria: item.MesIntermediaria === undefined ? new Date().getMonth() + 1 : item.MesIntermediaria,
                anoIntermediaria: item.AnoIntermediaria === undefined ? new Date().getFullYear() : item.AnoIntermediaria,
                cadencia: isFlex ? (item.cadencia || prev[index]?.cadencia || 3) : undefined
            })));
        }
    }, [initialIntermediates]);

    useEffect(() => {
        if (onIntermediatesChange) {
            const apiFormat = intermediates.map(item => ({
                Valor: Number.parseFloat(item.value) || 0,
                MesIntermediaria: item.mesIntermediaria,
                AnoIntermediaria: item.anoIntermediaria
            }));

            onIntermediatesChange(apiFormat);
        }
    }, [intermediates, onIntermediatesChange]);

    // Adicionar nova parcela intermediária
    const addIntermediate = (cadencia = null) => {
        if (intermediates.length >= limit) return;

        let newMonth, newYear, newValue, newCadencia;
        if (intermediates.length === 0) {
            const now = new Date();
            newMonth = now.getMonth() + initialMonthOffset;
            newYear = now.getFullYear();
            newValue = 0;
            newCadencia = isFlex ? 3 : undefined; // Padrão trimestral para Flex
            if (newMonth > 12) {
                newMonth -= 12;
                newYear++;
            }
        } else {
            // Pega os valores do primeiro registro para replicar apenas o valor
            const first = intermediates[0];
            const last = intermediates[intermediates.length - 1];
            
            // Se for Flex, adicionar baseado na cadência
            if (isFlex) {
                // Usar a cadência passada como parâmetro, ou a do último registro
                const cadenciaUsada = cadencia || last.cadencia || 3;
                newMonth = last.mesIntermediaria + cadenciaUsada;
                newYear = last.anoIntermediaria;
                if (newMonth > 12) {
                    newMonth -= 12;
                    newYear += Math.floor((last.mesIntermediaria + cadenciaUsada - 1) / 12);
                }
                newCadencia = 3; // Próxima intermediária padrão trimestral
            } else {
                // Para Carteira, adiciona apenas um mês (comportamento original)
                newMonth = last.mesIntermediaria + 1;
                newYear = last.anoIntermediaria;
                if (newMonth > 12) {
                    newMonth -= 12;
                    newYear++;
                }
                newCadencia = undefined;
            }
            newValue = first.value;
        }

        const newIntermediate = {
            id: Date.now(),
            description: `Parcela Intermediária ${intermediates.length + 1}`,
            value: newValue,
            mesIntermediaria: newMonth,
            anoIntermediaria: newYear,
            cadencia: newCadencia
        };

        // Todos os registros devem ter o mesmo valor, mas mês e ano independentes
        const updated = [...intermediates, newIntermediate].map((item, idx, arr) => ({
            ...item,
            value: arr[0].value,
            description: `Parcela Intermediária ${idx + 1}`
        }));
        setIntermediates(updated);
    };

    // Remove uma intermediária e mantém todos os valores iguais ao primeiro, mas mês/ano independentes
    const removeIntermediate = (idToRemove) => {
        markEditTime();
        setIntermediates(prev => {
            const updatedList = prev.filter(item => item.id !== idToRemove);
            if (updatedList.length === 0) return [];
            return updatedList.map((item, idx, arr) => ({
                ...item,
                value: arr[0].value,
                description: `Parcela Intermediária ${idx + 1}`
            }));
        });
    };

    // Atualiza todos os campos de todas as intermediárias para o mesmo valor do editado
    const updateAllIntermediates = (field, newValue) => {
        setIntermediates(prev => prev.map((item, idx) => ({
            ...item,
            [field]: newValue,
            description: `Parcela Intermediária ${idx + 1}`
        })));
    };

    const updateIntermediateValue = (id, newValue) => {
        markEditTime();
        if (replicateValue) {
            updateAllIntermediates('value', Number.parseFloat(newValue) || 0);
        } else {
            setIntermediates(prev => prev.map(item =>
                item.id === id ? { ...item, value: Number.parseFloat(newValue) || 0 } : item
            ));
        }
    };

    const updateIntermediateField = (id, field, newValue) => {
        markEditTime();
        if (field === 'value' && replicateValue) {
            updateAllIntermediates('value', Number.parseFloat(newValue) || 0);
        } else {
            setIntermediates(prev => prev.map(item =>
                item.id === id ? { ...item, [field]: newValue } : item
            ));
        }
    };

    const isLimitReached = intermediates.length >= limit;

    return {
        intermediates,
        addIntermediate,
        removeIntermediate,
        updateIntermediateValue,
        updateIntermediateField,
        isLimitReached
    };
};
